import type { DomainId } from "./lib/domain-id";

export { CONSULTATION_TYPE_SYSTEM } from "@repo/fhir-systems/code-systems";

import { PreconditionFailedError } from "./errors";
import { normalizeDurationToMin } from "./lib/normalize-duration";

/** Stable HealthcareService domain identity, independent of the FHIR locator. */
export type HealthcareServiceId = DomainId<"HealthcareServiceId">;

export function asHealthcareServiceId(value: string): HealthcareServiceId {
  if (!value) throw new PreconditionFailedError("A HealthcareService identity cannot be empty");
  return value as HealthcareServiceId;
}

// ============================================================
// HealthcareService — a bookable service (ADR-0017)
//
// What a patient actually books. It exists because Medplum's availability search keys off
// Slot.serviceType, so a bookable thing needs a service to be booked as.
// ============================================================

export const HEALTHCARE_SERVICE_DURATION_UNITS = ["min", "h"] as const;
export type HealthcareServiceDurationUnit = (typeof HEALTHCARE_SERVICE_DURATION_UNITS)[number];

interface Duration {
  value: number;
  unit: HealthcareServiceDurationUnit;
}

/**
 * Coding system for consultation-type HealthcareServices.
 * Written to Appointment.appointmentType at booking time; used by the
 * data-store view to distinguish consultation visits from protocol visits.
 */

interface HealthcareServiceFields {
  active: boolean;
  /** How long a booking of this service takes. */
  duration: Duration;
  /**
   * Offered-start cadence — a 15-minute interval means only :00/:15/:30/:45
   * starts are shown. Distinct from a Schedule's own slot granularity, which
   * stays finer so chained segments can be resolved.
   */
  alignmentInterval?: Duration;
  /** The protocol's identifier code, mirrored onto schedules for rendering. */
  typeCode: { system: string; code: string; display?: string };
}

export interface CreateHealthcareServiceInput extends HealthcareServiceFields {
  id: string;
}

export interface HealthcareServiceProps extends HealthcareServiceFields {
  id: HealthcareServiceId;
}

export type StoredHealthcareServiceProps = Omit<HealthcareServiceProps, "active"> & { active?: boolean };

export class HealthcareService {
  private state: HealthcareServiceProps;

  private constructor(props: HealthcareServiceProps) {
    if (!props.typeCode.system || !props.typeCode.code) {
      throw new PreconditionFailedError("A healthcare service requires a type code with a system and a code");
    }
    assertPositive(props.duration, "duration");
    if (props.alignmentInterval) assertPositive(props.alignmentInterval, "alignment interval");
    this.state = structuredClone(props);
  }

  // ── facts ──

  static create(input: CreateHealthcareServiceInput): HealthcareService {
    return new HealthcareService({
      ...structuredClone(input),
      id: asHealthcareServiceId(input.id),
    });
  }

  static restore(snapshot: StoredHealthcareServiceProps): HealthcareService {
    // An unflagged stored service is not offerable: bookable has to be asserted,
    // never assumed from a row that never said either way.
    return new HealthcareService({ ...snapshot, active: snapshot.active ?? false });
  }

  get id(): HealthcareServiceId {
    return this.state.id;
  }

  get isActive(): boolean {
    return this.state.active;
  }

  get durationMinutes(): number {
    return normalizeDurationToMin(this.state.duration);
  }

  /** Fanning a service out onto schedules only makes sense once it is live. */
  assertFanOutable(): void {
    if (!this.state.active) {
      throw new PreconditionFailedError(`Healthcare service ${this.state.id} is not active`);
    }
  }

  snapshot(): HealthcareServiceProps {
    return structuredClone(this.state);
  }

  // ── operations ──

  activate(): void {
    if (this.state.active) {
      throw new PreconditionFailedError("This healthcare service is already active");
    }
    this.state.active = true;
  }

  deactivate(): void {
    if (!this.state.active) {
      throw new PreconditionFailedError("This healthcare service is already inactive");
    }
    this.state.active = false;
  }

  setDuration(duration: Duration): void {
    assertPositive(duration, "duration");
    this.state.duration = duration;
  }

  /** Passing nothing clears the cadence, which means "offer every slot start". */
  setAlignmentInterval(alignmentInterval?: Duration): void {
    if (alignmentInterval) assertPositive(alignmentInterval, "alignment interval");
    this.state.alignmentInterval = alignmentInterval;
  }
}

function assertPositive(duration: Duration, label: string): void {
  if (normalizeDurationToMin(duration) <= 0) {
    throw new PreconditionFailedError(`Healthcare service ${label} must be positive`);
  }
}
